import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/free-mode';
import { FreeMode } from 'swiper/modules';

const Visitech = () => {
  return (
    <div id="main-wrapper">
      <Header />

      <div id="content" role="main">

        {/* Hero Section */}
        <section
          className="hero-wrap"
          style={{
            position: 'relative',
            overflow: 'hidden',
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            backgroundImage: "url('/images/projects/rovovision.png')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="container" style={{ position: 'relative', zIndex: 2, paddingTop: '70px' }}>
            <div className="row">
              <div className="col-12 text-center text-white">
                <h1 className="text-16 font-weight-600 mb-3 text-white">Visitech</h1>
                <p className="lead text-white-100 mb-0">
                 A Digital Visitor Management System Ensuring Traceable, Compliant, and Safety-Driven Visitor Journeys with Real-Time Verification, Safety Training, and Automated Access Control.
                </p>
              </div>
            </div>
          </div>
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', zIndex: 1 }}></div>
        </section>

        {/* Blog Intro */}
        <section className="py-5 bg-white text-dark">
          <div className="container">
            <div className="row align-items-center mb-5 g-4 flex-column-reverse flex-md-row">
              <div className="col-md-6 mb-4 mb-md-0">
                <img 
                  src="/images/projects/visi1.png" 
                  alt="Robots on Factory Floor" 
                  className="img-fluid rounded-4 shadow-lg"
                  style={{ height: '350px', objectFit: 'cover', width: '100%' }}
                />
              </div>
              <div className="col-md-6">
                <h2 className="fw-bold mb-3">VISITECH: Real-Time Visitor Verification and Access Control</h2>
                <p className="text-black" style={{ fontSize: "1rem" }}>
                  VISITECH is a smart visitor management and access control solution designed for high-volume, high-risk environments. It ensures that every visitor is verified, educated, and controlled before entering a facility. By replacing manual and fragmented processes, VISITECH brings safety, compliance, and accountability into a single digital workflow.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What is Visitech */}
        <section className="py-5 bg-light text-dark">
          <div className="container">
            <div className="row mb-4">
              <div className="col-lg-10 mx-auto text-center">
                <h2 className="fw-bold mb-3">What is Visitech?</h2>
                <p className="text-black" style={{ fontSize: "1rem" }}>
                  VISITECH is a visitor verification and access control system designed to verify visitors, educate them on safety, and control entry in high-volume, high-risk environments. It addresses the lack of traceability, untrained visitor entry, and compliance gaps found in traditional manual and badge-based systems.
                </p>
              </div>
            </div>

            <div className="row g-4">
              {[{
                  title: "No Traceability",
                  text: "Manual systems create blind spots in visitor tracking, making it difficult to know who is inside, why they are there, and for how long.",
                  icon: "bi bi-display",
                  img: "/images/projects/not.png"
                },
                {
                  title: "Digital Visitor Registration",
                  text: "VISITECH creates a digital visitor identity capturing name and company, purpose of visit, host details, time-stamped entry records, and photo capture.",
                  icon: "bi bi-activity",
                  img: "/images/projects/digi.png"
                },
                {
                  title: "Emergency Accountability Failure",
                  text: "In emergency situations, manual records fail to provide real-time accountability of visitors inside the premises.",
                  icon: "bi bi-robot",
                  img: "/images/projects/empr.png"
                },
                {
                  title: "Manual & Fragmented Systems",
                  text: "Traditional visitor systems are manual, fragmented, and inflexible, leading to human errors and poor access control.",
                  icon: "bi bi-clock",
                  img: "/images/projects/maf.png"
                },
              ].map((feature, idx) => (
                <div className="col-12 col-md-6 col-lg-6 mb-4" key={idx}>
                  <div className="card h-100 border-0 shadow-lg rounded-4 overflow-hidden">
                    <img 
                      src={feature.img} 
                      alt={feature.title} 
                      className="card-img-top"
                      style={{ height: '250px', objectFit: 'cover', width: '100%' }} 
                    />
                    <div className="card-body">
                      <div className="mb-2 text-primary">
                        <i className={`${feature.icon} fs-2`}></i>
                      </div>
                      <h5 className="fw-bold mb-3">{feature.title}</h5>
                      <p className="text-muted mb-0">{feature.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Business Impact */}
        <section className="py-5 bg-light text-dark">
          <div className="container">
            <div className="row align-items-center g-4 flex-column-reverse flex-md-row">
              <div className="col-md-6">
                <h2 className="fw-bold mb-4">Business Impact</h2>
                <p className="text-black" style={{ fontSize: "1.50rem" }}>
                  VISITECH significantly reduces safety risks and compliance failures by eliminating uncontrolled visitor entry. Organizations gain:
                </p>
                <ul className="list-unstyled text-muted fs-20">
                  <li className="mb-1">Real-time visibility of all visitors</li>
                  <li className="mb-1">Improved safety enforcement</li>
                  <li className="mb-1">Audit-ready compliance records</li>
                  <li className="mb-1">Reduced human error in security operations</li>
                  <li className="mb-2">Scalable control for high daily visitor volumes</li>
                </ul>
              </div>
              <div className="col-md-6">
                <img
                  src="/images/projects/impb.png"
                  alt="Business Impact"
                  className="img-fluid rounded-4 shadow-lg"
                  style={{ height: '400px', objectFit: 'cover', width: '100%' }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Horizontal Scrollable Features */}
        <section className="py-5 bg-white text-dark">
          <div className="container">
            <div className="row mb-4">
              <div className="col-lg-10 mx-auto text-center">
                <h2 className="fw-bold mb-3">Key Features & Capabilities</h2>
                <p className="text-muted mb-5">
                  RovoConnect maximizes efficiency, visibility, and predictive control across your manufacturing floor.
                </p>
              </div>
            </div>

            <div
              className="d-flex flex-row flex-nowrap"
              style={{ gap: "1rem", paddingBottom: "1rem", overflowX: "auto", scrollbarWidth: "none", msOverflowStyle: "none" }}
              onWheel={(e) => { e.currentTarget.scrollLeft += e.deltaY; }}
            >
              <style>
                {`
                  div::-webkit-scrollbar { display: none; }

                  @media (max-width: 768px) {
                    .card { min-width: 250px !important; }
                    .card img { height: 180px !important; }
                  }

                  @media (max-width: 576px) {
                    .card { min-width: 200px !important; }
                    .card img { height: 150px !important; }
                  }
                `}
              </style>

              {[{
                  title: "Digital Visitor Registration",
                  text: "Creates a time-stamped digital identity with host details and optional photo capture",
                  icon: "bi bi-collection",
                  img: "/images/projects/digiv.png",
                },
                {
                  title: "Safety Awareness Training",
                  text: "Mandatory site-specific safety videos with quiz validation.",
                  icon: "bi bi-speedometer2",
                  img: "/images/projects/safe1.png",
                },
                {
                  title: "Pass / Fail Access Logic",
                  text: "Only safety-approved visitors are granted entry.",
                  icon: "bi bi-bar-chart",
                  img: "/images/projects/fail.png",
                },
                {
                  title: "QR-Based Workflow",
                  text: "Seamless and fast visitor movement.",
                  icon: "bi bi-controller",
                  img: "/images/projects/qr1.png",
                },
                {
                  title: "Visitor Dashboard",
                  text: "Real-time visibility for security and administrators.",
                  icon: "bi bi-layers",
                  img: "/images/projects/visid.png",
                },
                {
                  title: "Minimal Infrastructure",
                  text: "Uses entry kiosks and visitors’ smartphones.",
                  icon: "bi bi-layers",
                  img: "/images/projects/mini.png",
                }].map((feature, idx) => (
                  <div key={idx} className="card flex-shrink-0 border-0 shadow-lg rounded-4 overflow-hidden" style={{ width: "300px" }}>
                    <img src={feature.img} alt={feature.title} className="card-img-top" style={{ height: "200px", objectFit: "cover" }} />
                    <div className="card-body">
                      <div className="mb-2 text-primary">
                        <i className={`${feature.icon} fs-2`}></i>
                      </div>
                      <h5 className="fw-bold mb-2">{feature.title}</h5>
                      <p className="text-muted mb-0">{feature.text}</p>
                    </div>
                  </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Visitech */}
        <section className="py-5 bg-light text-dark">
          <div className="container">
            <div className="row align-items-center g-4 flex-column-reverse flex-md-row">
              <div className="col-md-6">
                <img
                  src="/images/projects/visi1.png"
                  alt="Why Choose Visitech"
                  className="img-fluid rounded-4 shadow-lg mb-4"
                  style={{ height: '400px', objectFit: 'cover', width: '100%' }}
                />
              </div>
              <div className="col-md-6">
                <h2 className="fw-bold mb-4">Why Choose Visitech?</h2>
                <p className="text-muted mb-4">
                  VISITECH goes beyond traditional systems by delivering maximum control with minimal infrastructure. Unlike manual badge systems that fail during emergencies and audits, VISITECH provides:
                </p>
                <ul className="list-unstyled text-muted fs-6">
                  <li className="mb-3">Full traceability and accountability.</li>
                  <li className="mb-3">Zero compromise on safety rules.</li>
                  <li className="mb-3">Proof of compliance at all times.</li>
                  <li className="mb-3">Reduced dependency on human judgment.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Conclusion & CTA */}
        <section className="py-5 bg-dark text-white text-center">
          <div className="container">
            <p className="lead mb-4 mx-auto" style={{ maxWidth: '800px' }}>
              VISITECH transforms visitor access from a security risk into a controlled, intelligent, and compliant process. By integrating verification, safety education, and automated access control, VISITECH ensures that only trained and approved visitors enter your facility—protecting people, assets, and compliance with confidence.
            </p>
            <a href="/contact" className="btn btn-primary btn-lg rounded-pill px-5">Inquire About Partnership</a>
          </div>
        </section>

      </div>

      <Footer />
    </div>
  );
};

export default Visitech;
